import { useState } from 'react';
import type { OrganizationUser } from '../../types';
import RoleBadge from '../common/RoleBadge';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useRemoveUser } from '../../hooks/useUsers';

interface Props {
  user: OrganizationUser;
  isCurrentUser?: boolean;
  canRemove?: boolean;
}

export default function UserRow({ user, isCurrentUser, canRemove }: Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const { mutate: removeUser, isPending, error, reset } = useRemoveUser();

  const joinedDate = new Date(user.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Never offer removal of yourself.
  const showRemove = canRemove && !isCurrentUser;

  const openConfirm = () => {
    reset();
    setReason('');
    setShowConfirm(true);
  };

  const handleRemove = () => {
    removeUser(
      { userId: user.id, reason },
      { onSuccess: () => setShowConfirm(false) }
    );
  };

  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium">
          {user.email.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {user.email}
            {isCurrentUser && (
              <span className="ml-2 text-xs text-gray-500">(you)</span>
            )}
          </div>
          <div className="text-sm text-gray-500">Joined {joinedDate}</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <RoleBadge role={user.role} />
        {showRemove && (
          <button
            onClick={openConfirm}
            aria-label={`Remove ${user.email}`}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        )}
      </div>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Remove user">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Remove <span className="font-medium text-gray-900">{user.email}</span> from your
            organization? They will lose access on their next request. Our team can restore the
            account if this was a mistake.
          </p>

          <div>
            <label htmlFor="remove-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="remove-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. left the company"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error instanceof Error ? error.message : 'Failed to remove user'}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowConfirm(false)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleRemove}
              disabled={isPending}
              aria-label="Remove user"
              className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isPending ? <Spinner size="sm" /> : 'Remove user'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
